<template>
  <button
    :type="type"
    class="v3-base-btn"
    v-bind="$attrs"
    @click="$emit('click', $event)"
    :class="{
      'full-rounded': rounded,
      solid,
      'solid-gradient': solidGradient,
      'solid-gray': solidGray,
      disabled: isDisabled
    }"
    :style="buttonStyle"
    :disabled="isDisabled"
    :id="nativeId || undefined"
    :role="nativeRole || undefined"
    :aria-label="resolvedAriaLabel"
    :aria-busy="ariaBusy ? 'true' : undefined"
    :aria-selected="ariaSelected !== undefined ? ariaSelected : undefined"
    :aria-pressed="ariaPressed !== undefined ? ariaPressed : undefined"
  >
    <div class="d-flex align-items-center">
      <div
        v-if="prependIcon"
        class="btn-icon prepened lh-normal me-2"
        aria-hidden="true"
      >
        <i class="lh-normal" :class="prependIcon" aria-hidden="true"></i>
      </div>
      <div class="btn-label lh-normal">
        <slot></slot>
      </div>
      <div
        v-if="appendIcon"
        class="btn-icon append lh-normal ms-2"
        aria-hidden="true"
      >
        <i class="lh-normal" :class="appendIcon" aria-hidden="true"></i>
      </div>
    </div>
  </button>
</template>

<script>
import { computed, useAttrs } from "vue"

export default {
  inheritAttrs: false,
  emits: ["click"],
  props: {
    prependIcon: String,
    appendIcon: String,
    rounded: Boolean,
    solid: Boolean,
    solidGradient: Boolean,
    solidGray: Boolean,
    customColor: String,
    isDisabled: {
      type: Boolean,
      default: false
    },
    addCustomStyle: {
      type: Boolean,
      default: false
    },
    /** Native HTML `type` for `<button>` (`button`, `submit`, `reset`). */
    type: {
      type: String,
      default: "button",
      validator: (v) => ["button", "submit", "reset"].includes(v)
    },
    /** Use when the button has no visible text (icon-only); overrides slot for the accessible name. */
    ariaLabel: {
      type: String,
      default: ""
    },
    /** Indicates an action is in progress (e.g. loading). */
    ariaBusy: {
      type: Boolean,
      default: false
    },
    /** For tab widgets (`role="tab"`) — stable `id` for `aria-controls` / roving tabindex. */
    nativeId: {
      type: String,
      default: ""
    },
    nativeRole: {
      type: String,
      default: ""
    },
    /** `'true'` | `'false'` when using `role="tab"`. */
    ariaSelected: {
      type: String,
      default: undefined
    },
    /** Omit when using tabs (use `ariaSelected` instead). */
    ariaPressed: {
      type: String,
      default: undefined
    }
  },
  setup(props) {
    const attrs = useAttrs()
    const resolvedAriaLabel = computed(() => {
      const fromProp = (props.ariaLabel || "").trim()
      if (fromProp) return fromProp
      const fromAttrs = attrs["aria-label"]
      if (typeof fromAttrs === "string" && fromAttrs.trim()) {
        return fromAttrs.trim()
      }
      return undefined
    })

    const buttonStyle = computed(() => {
      if (!props.addCustomStyle) {
        return {}
      }

      if (props.isDisabled) {
        return {
          backgroundColor: "gray",
          borderColor: "gray",
          cursor: "not-allowed"
        }
      }

      return {
        backgroundColor: props.customColor,
        borderColor: props.customColor
      }
    })

    return {
      buttonStyle,
      resolvedAriaLabel
    }
  }
}
</script>
